<!-- RELEASE REGION SELECTION MODAL -->
<template>
  <b-modal
    id="ptvReleaseModal"
    content-class="release-modal"
    size="lg"
    hide-footer
    hide-header-close
    no-close-on-backdrop
    centered
  >
    <!-- Custom Header -->
    <template #modal-header>
      <div class="rm-header d-flex align-items-center w-100">
        <div class="rm-header-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#4db8ff" stroke-width="1.5"/>
            <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" stroke="#4db8ff" stroke-width="1.5"/>
            <path d="M4.93 4.93a16 16 0 0 1 14.14 0M4.93 19.07a16 16 0 0 0 14.14 0" stroke="#4db8ff" stroke-width="1.5"/>
          </svg>
        </div>
        <div class="rm-header-text">
          <h5 class="rm-title mb-0">Select a Region</h5>
          <p class="rm-subtitle mb-0">Choose a dataset to explore on the map</p>
        </div>
        <div class="rm-header-badge ml-auto">
          {{ releases.length }} region{{ releases.length !== 1 ? 's' : '' }} available
        </div>
      </div>
    </template>

    <!-- Region Cards Grid -->
    <b-container fluid class="rm-body">
      <b-row v-if="releases.length === 0" class="justify-content-center">
        <b-col cols="12" class="text-center rm-empty">
          <div class="rm-empty-icon">◎</div>
          <p>No regions available</p>
        </b-col>
      </b-row>

      <b-row class="rm-grid">
        <b-col
          v-for="(release, index) in releases"
          :key="release.id"
          cols="12"
          sm="6"
          class="rm-card-col mb-3"
        >
          <div class="rm-card" @click="datasetSelected(release)">
            <!-- Map Preview -->
            <div class="rm-map-preview">
              <div class="rm-map-glow"></div>
              <img
                v-if="getRegionImage(release.extent_description)"
                :src="getRegionImage(release.extent_description)"
                :alt="release.extent_description"
                class="rm-map-img"
              />
              <div v-else class="rm-map-placeholder">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="rm-globe-fallback">
                  <circle cx="12" cy="12" r="10" stroke="#4db8ff" stroke-width="1.5"/>
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" stroke="#4db8ff" stroke-width="1.5"/>
                </svg>
              </div>
            </div>

            <!-- Card Info -->
            <div class="rm-card-info">
              <div class="rm-region-name">{{ release.extent_description }}</div>
              <div class="rm-release-date">
                <span class="rm-date-dot"></span>
                {{ formatDate(release.release_date) }}
              </div>
            </div>

            <!-- Select CTA -->
            <div class="rm-card-action">
              <span class="rm-action-label">Select</span>
              <svg class="rm-action-arrow" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>

            <!-- Hover scan line effect -->
            <div class="rm-card-shine"></div>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </b-modal>
</template>

<style>
.bg_color{
  background:#063c6d;
}
/* ── Modal shell ── */
.release-modal .modal-content {
  background: transparent;
  border: none;
}

.release-modal .modal-header {
  background: #063c6d;
  color: white;
  border-bottom: 1px solid rgba(77, 184, 255, 0.2);
  padding: 1.1rem 1.4rem;
}

.release-modal .modal-body {
  /*background: linear-gradient(180deg, #052d55 0%, #041e3a 100%);*/
  border-radius: 0 0 12px 12px;
  padding: 0;
}

/* ── Header ── */
.rm-header { gap: 0.875rem;}

.rm-header-icon {
  width: 2.5rem;
  height: 2.5rem;
  background: rgba(77, 184, 255, 0.12);
  border: 1px solid rgba(77, 184, 255, 0.3);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rm-header-icon svg { width: 1.375rem; height: 1.375rem; }

.rm-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #e8f4ff;
  letter-spacing: 0.02em;
}

.rm-subtitle {
  font-size: 0.72rem;
  letter-spacing: 0.01em;
}

.rm-header-badge {
  background: rgba(77, 184, 255, 0.12);
  border: 1px solid rgba(77, 184, 255, 0.25);
  color: #4db8ff;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 20px;
  white-space: nowrap;
  letter-spacing: 0.02em;
}

/* ── Body / grid ── */
.rm-body {
  padding: 1.2rem 1.2rem 0.8rem;
}

.rm-grid { margin: 0 -0.5rem; }
.rm-card-col { padding: 0 0.5rem; }

/* ── Card ── */
.rm-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(77, 184, 255, 0.15);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.rm-card:hover {
  transform: translateY(-2px);
  border-color: rgba(77, 184, 255, 0.55);
  box-shadow: 0 8px 32px rgba(4, 150, 233, 0.2), 0 0 0 1px rgba(77, 184, 255, 0.2) inset;
}

.rm-card:hover .rm-card-shine {
  left: 120%;
}

.rm-card:hover .rm-map-glow {
  opacity: 1;
}

.rm-card:hover .rm-action-label {
  color: #4db8ff;
}

.rm-card:hover .rm-action-arrow {
  color: #4db8ff;
  transform: translateX(3px);
}

/* Shine sweep */
.rm-card-shine {
  content: '';
  position: absolute;
  top: 0;
  left: -60%;
  width: 40%;
  height: 100%;
  background: linear-gradient(105deg, transparent, rgba(255,255,255,0.06), transparent);
  transform: skewX(-15deg);
  transition: left 0.5s ease;
  pointer-events: none;
}

/* ── Map preview panel ── */
.rm-map-preview {
  position: relative;
  width: 6.875rem;
  min-width: 6.875rem;
  height: 5.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #041e3a 0%, #063c6d 60%, #0a5499 100%);
  border-right: 1px solid rgba(77, 184, 255, 0.1);
  overflow: hidden;
  flex-shrink: 0;
}

.rm-map-glow {
  position: absolute;
  width: 4.375rem;
  height: 4.375rem;
  background: radial-gradient(circle, rgba(4, 150, 233, 0.25) 0%, transparent 70%);
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.rm-map-img {
  width: 4.5rem;
  height: auto;
  max-height: 4.25rem;
  object-fit: contain;
  filter: drop-shadow(0 2px 8px rgba(4, 150, 233, 0.4)) brightness(1.05);
  position: relative;
  z-index: 1;
  transition: filter 0.2s ease, transform 0.2s ease;
}

.rm-card:hover .rm-map-img {
  filter: drop-shadow(0 4px 14px rgba(4, 150, 233, 0.65)) brightness(1.15);
  transform: scale(1.05);
}

.rm-map-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
}

.rm-globe-fallback { width: 3rem; height: 3rem; }

/* ── Card info ── */
.rm-card-info {
  flex: 1;
  padding: 0 0.875rem;
  min-width: 0;
}

.rm-region-name {
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.3125rem;
}

.rm-release-date {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.72rem;
  font-weight: 500;
}

.rm-date-dot {
  width: 0.3125rem;
  height: 0.3125rem;
  background: #4db8ff;
  border-radius: 50%;
  flex-shrink: 0;
  opacity: 0.7;
}

/* ── Action area ── */
.rm-card-action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 0 0.875rem 0 0.375rem;
  flex-shrink: 0;
}

.rm-action-label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: color 0.2s ease;
}

.rm-action-arrow {
  width: 1.125rem;
  height: 1.125rem;
  transition: color 0.2s ease, transform 0.2s ease;
}

/* ── Empty state ── */
.rm-empty { padding: 2.5rem 0; color: rgba(180, 210, 240, 0.4); }
.rm-empty-icon { font-size: 2.5rem; margin-bottom: 0.5rem; opacity: 0.4; }
</style>

<script>
import axios from 'axios'
import australiaImg from '../../../assets/extents/Australia.svg'
import europeImg from '../../../assets/extents/Europe.svg'
import japanImg from '../../../assets/extents/Japan.svg'
import middleEastImg from '../../../assets/extents/MiddleEast.svg'
import newZealandImg from '../../../assets/extents/NewZealand.svg'
import northAmericaImg from '../../../assets/extents/NorthAmerica.svg'
import southKoreaImg from '../../../assets/extents/SouthKorea.svg'

const REGION_MAP = [
  { keys: ['australia'],                   img: australiaImg },
  { keys: ['europe'],                      img: europeImg },
  { keys: ['japan'],                       img: japanImg },
  { keys: ['middle east', 'middleeast'],   img: middleEastImg },
  { keys: ['new zealand', 'newzealand'],   img: newZealandImg },
  { keys: ['north america', 'northamerica', 'usa', 'united states'], img: northAmericaImg },
  { keys: ['south korea', 'southkorea', 'korea'], img: southKoreaImg },
]

export default {
  name: 'DT_ReleaseModal',
  props: ['project_id'],
  data() {
    return {
      releases: [],
    }
  },
  methods: {
    datasetSelected(release) {
      this.$emit('datasetSelected', release)
    },
    formatDate(dateStr) {
      if (!dateStr) return '—'
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      })
    },
    getRegionImage(extentDescription) {
      if (!extentDescription) return null
      const lower = extentDescription.toLowerCase()
      for (const entry of REGION_MAP) {
        if (entry.keys.some(k => lower.includes(k))) return entry.img
      }
      return null
    },
    getReleases() {
      if (sessionStorage.token) {
        axios
          .get(process.env.API_LOCATION + '/api/release/project/' + this.project_id, {
            headers: { 'x-auth-token': sessionStorage.token }
          })
          .then(response => { this.releases = response.data })
          .catch(error => { console.log(error) })
      }
    },
  },
  mounted() {},
  watch: {
    project_id(newValue) {
      if (newValue) this.getReleases();
    }
  }
}
</script>
